import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { IDLE_STORAGE_KEY } from '../hooks/useIdleLogout';

// Create the context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        
        if (token && storedUser) {
            try {
                setUser(JSON.parse(storedUser));
                // Verify token validity
                verifyToken(token);
            } catch (err) {
                console.error('Error parsing stored user:', err);
                logout();
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
        // Runs once on mount to restore a stored session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const verifyToken = async (token) => {
        try {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const response = await api.get('/auth/verify');
            if (response.data.user) {
                setUser(response.data.user);
                localStorage.setItem('user', JSON.stringify(response.data.user));
            }
        } catch (err) {
            console.error('Token verification failed:', err);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        try {
            setError(null);
            const response = await api.post('/auth/login', { email, password });
            const { token, user } = response.data;
            
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            // A fresh login starts a new idle-timeout clock. Without this, a
            // stale timestamp left over from the *previous* session (the one
            // that just triggered the 3h idle logout) makes useIdleLogout see
            // an already-expired session on mount and log the user straight
            // back out, looping them back to /login.
            localStorage.setItem(IDLE_STORAGE_KEY, String(Date.now()));
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            setUser(user);
            return { success: true, user };
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed');
            throw err;
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem(IDLE_STORAGE_KEY);
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
    };

    /** Merge a partial user update (e.g. a new avatar_path) into the current
     * session without a full re-login. */
    const updateUser = (partial) => {
        setUser(prev => {
            const next = { ...prev, ...partial };
            localStorage.setItem('user', JSON.stringify(next));
            return next;
        });
    };

    const value = {
        user,
        loading,
        error,
        login,
        logout,
        updateUser,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;